import { useState, type CSSProperties, type ReactNode } from "react";
import type { ClubKit } from "@legacy-manager/engine";
import type { PlayerDetail } from "../game/types";
import { Bar, Crest, MoraleFace, PositionChip, strengthColor } from "../ui/atoms";
import { EscudoDoClube } from "../ui/brasoes/Escudo";
import { FigurinhaDoJogador } from "../ui/avatar/Figurinha";
import { AvatarDoJogador } from "../ui/avatar/Avatar";
import { useDialogoDeAtalhos } from "../ui/useAtalhos";
import {
  dinheiro,
  motivoDoMotor,
  notaDeLaudo,
  rotuloDeCaracteristica,
  rotuloDePe,
  rotuloDePersonalidade,
  rotuloDoMotor,
  useT,
  type Chave,
} from "../i18n";

/**
 * Ficha do jogador.
 *
 * É aqui que os quatorze atributos por trás da força aparecem. A tela de elenco
 * mostra os dois destaques, como Brasfoot e Elifoot faziam; quem quiser o
 * detalhe encontra aqui, com as características que pesam na posição marcadas.
 *
 * **A mesma ficha mora em dois lugares.** No elenco do computador ela fica numa
 * coluna ao lado da tabela, e clicar num jogador troca o que está ali; em todo
 * o resto ela abre por cima, porque não sobra coluna. O conteúdo é o mesmo
 * componente nos dois casos: o que muda é a moldura, e duas cópias da ficha
 * seriam duas fichas para manter iguais.
 */
export interface DadosDaFicha {
  player: PlayerDetail;
  cash: number;
  marketOpen: boolean;
  busy: boolean;
  onOffer: (amount: number, salary: number, years: number, installments: number) => void;
  /** Compra na hora. Nulo onde não existe, como na liga online. */
  onBuy: ((years: number) => void) | null;
  onScout: () => void;
  onLoan: (salaryShare: number) => void;
  onAction: (action: string) => void;
  onClose: () => void;
  /** Ajuste da moldura, para a ficha que fica na coluna e não por cima. */
  estilo?: CSSProperties;
  /** A ficha está na coluna do elenco, e não cobrindo a tela. */
  naColuna?: boolean;
  /**
   * O uniforme do clube de quem olha, para a camisa com o número.
   *
   * Só vale para jogador do próprio elenco: a camisa é a nossa, e vesti-la num
   * jogador de outro clube seria mostrar uma coisa que não é verdade.
   */
  uniforme?: ClubKit | null;
  /**
   * O escudo do clube, para a parede do vestiário.
   *
   * Chega como função e não como elemento pronto porque quem monta a ficha não
   * sabe de que tamanho a cena o quer, e a cena não sabe se o clube tem escudo
   * desenhado, escudo de catálogo ou só as duas cores. Cada um responde o que
   * lhe cabe.
   */
  escudo?: (size: number, semSigla?: boolean) => ReactNode;
  /** A sigla do clube, que a figurinha escreve embaixo do escudo. */
  sigla?: string;
}

/**
 * A ficha por cima da tela.
 *
 * O fundo escuro e o `Esc` moram aqui, e não no conteúdo: quem está na coluna
 * do elenco não tem diálogo nenhum para fechar, e registrar um ali tiraria os
 * atalhos do hub de quem só clicou num jogador.
 */
export function PlayerSheet(dados: DadosDaFicha) {
  const t = useT();

  useDialogoDeAtalhos({ nome: "Ficha do jogador", nivel: 25, aoFechar: dados.onClose });

  return (
    <div
      role="dialog"
      aria-label={t("ficha.aria", { nome: dados.player.name })}
      onClick={dados.onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--gutter)",
        zIndex: 25,
      }}
    >
      <FichaDoJogador
        {...dados}
        estilo={{ width: "min(620px, 100%)", maxHeight: "88vh", overflow: "auto" }}
      />
    </div>
  );
}

export function FichaDoJogador({
  player,
  cash,
  marketOpen,
  busy,
  onOffer,
  onBuy,
  onScout,
  onLoan,
  onAction,
  onClose,
  estilo,
  naColuna,
  uniforme,
  escudo,
  sigla,
}: DadosDaFicha) {
  const t = useT();
  const suggestedPrice = Math.round(player.askingPrice * 1.15);
  const suggestedSalary = Math.round((player.askingSalary ?? player.salary) * 1.05);
  const [amount, setAmount] = useState(suggestedPrice);
  const [salary, setSalary] = useState(suggestedSalary);
  const [years, setYears] = useState(3);
  const [installments, setInstallments] = useState(1);

  // Só a entrada sai do caixa agora. É por ela que o treinador decide se cabe.
  const entrada = installments === 1 ? amount : Math.round(amount / installments);

  const [abaDeAtributos, definirAbaDeAtributos] = useState<"pesam" | "demais">("pesam");
  const [secao, definirSecao] = useState<"proposta" | "emprestimo" | "caracteristicas" | "olheiro">(
    "proposta",
  );
  // Empréstimo só existe para quem tem clube e topa conversar.
  const podeEmprestimo = Boolean(player.clubId) && player.askingSalary !== null;
  const abasDeFora = [
    { chave: "proposta" as const, rotulo: t("ficha.abaProposta") },
    ...(podeEmprestimo ? [{ chave: "emprestimo" as const, rotulo: t("ficha.abaEmprestimo") }] : []),
    { chave: "caracteristicas" as const, rotulo: t("ficha.secaoCaracteristicas") },
    { chave: "olheiro" as const, rotulo: t("ficha.olheiro") },
  ];
  const abaDeFora = secao === "emprestimo" && !podeEmprestimo ? "proposta" : secao;

  const weighted = player.attrs.filter((attr) => attr.weighted);
  const others = player.attrs.filter((attr) => !attr.weighted);

  /*
   * Com figurinha, os dados moram nela.
   *
   * Nome, idade, pé e personalidade no rodapé; moral, físico e média de um lado
   * da foto e a temporada do outro, como as fichas do campo da tática. A linha
   * de nome com posição e força, que repetia o rodapé, e o quadro da temporada
   * embaixo da foto saem, e a ficha cabe na altura da tela.
   */
  const comFigurinha = Boolean(uniforme && player.isMine);

  return (
    <div
      className={`card ficha${naColuna ? " ficha-na-coluna" : ""}${comFigurinha ? " ficha-com-figurinha" : ""}`}
      style={{ padding: comFigurinha || naColuna ? 14 : 20, ...estilo }}
      onClick={(evento) => evento.stopPropagation()}
    >
      {/*
        A figurinha do jogador, no alto da ficha.

        O conceito tem um desenho do jogador, que não existe: são trezentos e
        vinte clubes e milhares de jogadores gerados, e nenhum tem retrato. No
        lugar dele vai um avatar do banco, escolhido pelo id e pela
        nacionalidade dele, vestido com o uniforme que já está no mundo.

        Só para jogador do próprio elenco: vestir a nossa camisa num jogador de
        outro clube seria mostrar o que não é.
      */}
      {comFigurinha && uniforme && (
        <div className="ficha-foto-area">
          <FigurinhaDoJogador
            playerId={player.id}
            nacionalidade={player.nationality}
            uniforme={uniforme}
            escudo={escudo}
            sigla={sigla}
            numero={player.shirt}
            posicao={player.pos}
            forca={player.strength}
            nome={player.name}
            subtitulo={[
              t("tatica.idadeDoCandidato", { idade: player.age }),
              rotuloDePe(player.foot),
              rotuloDePersonalidade(player.personality),
            ].join(" · ")}
          >
            <div className="ficha-sobre ficha-sobre-esquerda">
              <SeloNaFoto rotulo={t("ficha.moral")}>
                <MoraleFace value={player.morale} size={14} />
                {player.morale}
              </SeloNaFoto>
              <SeloNaFoto
                rotulo={t("ficha.fisico")}
                cor={
                  player.condition >= 85
                    ? "var(--primary)"
                    : player.condition >= 70
                      ? "var(--warning)"
                      : "var(--danger)"
                }
              >
                {player.condition}%
              </SeloNaFoto>
              {player.rating > 0 && (
                <SeloNaFoto rotulo={t("tatica.media")}>{player.rating.toFixed(1)}</SeloNaFoto>
              )}
            </div>
            <div className="ficha-sobre ficha-sobre-direita">
              <SeloNaFoto rotulo={t("ficha.temporada.jogos")}>{player.apps}</SeloNaFoto>
              <SeloNaFoto rotulo={t("tatica.golsAssist")}>
                {player.goals}/{player.assists}
              </SeloNaFoto>
              <SeloNaFoto rotulo={t("ficha.temporada.cartoes")}>
                <span className="ficha-cartao amarelo">{player.yellow}</span>
                <span className="ficha-cartao vermelho">{player.red}</span>
              </SeloNaFoto>
            </div>
          </FigurinhaDoJogador>
        </div>
      )}

      {!comFigurinha && (
      <>
      <div className="row" style={{ justifyContent: "space-between", gap: 12 }}>
        <div className="row" style={{ gap: 12, minWidth: 0 }}>
          <span className="ficha-foto-mini">
            <AvatarDoJogador playerId={player.id} nacionalidade={player.nationality} rosto />
            <span className="ficha-foto-mini-pos">
              <PositionChip pos={player.pos} />
            </span>
            {/* O escudo do clube dele no canto, como a figurinha faz com o nosso. */}
            {player.clubId && player.clubColors && (
              <span className="ficha-foto-mini-escudo" title={player.clubName}>
                {player.clubCrest ? (
                  <EscudoDoClube crest={player.clubCrest} short={player.clubShort} size={26} semSigla />
                ) : (
                  <Crest colors={player.clubColors} short={player.clubShort} size={26} />
                )}
              </span>
            )}
          </span>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: 18 }}>
              {player.name}
              {player.star && (
                <span style={{ color: "var(--accent)", fontSize: 12, marginLeft: 8, whiteSpace: "nowrap" }}>
                  {t("timeB.jogadorEstrela")}
                </span>
              )}
            </h2>
            <div className="muted" style={{ fontSize: 12 }}>
              {t("ficha.subtitulo", {
                // O jogador sem contrato chega com o clube vazio: a frase
                // dele é da tela, e não do worker, que não sabe o idioma.
                clube: player.clubId ? player.clubName : t("comum.semClube"),
                idade: player.age,
                pe: rotuloDePe(player.foot),
                personalidade: rotuloDePersonalidade(player.personality),
              })}
            </div>
          </div>
        </div>
        <span className="num" style={{ fontSize: 30, fontWeight: 800, color: strengthColor(player.strength) }}>
          {player.strength}
        </span>
      </div>

      {/*
        A temporada em quatro números.

        Jogos, gols, assistências e cartões são o que se olha para decidir se o
        cara está jogando, e estavam espalhados entre os fatos e o nada: os
        cartões o motor contava desde sempre e nenhuma tela mostrava.
      */}
      <div className="ficha-temporada ficha-temporada-compacta">
        <NumeroDaTemporada rotulo={t("ficha.temporada.jogos")} valor={String(player.apps)} />
        <NumeroDaTemporada rotulo={t("ficha.temporada.gols")} valor={String(player.goals)} />
        <NumeroDaTemporada
          rotulo={t("ficha.temporada.assistencias")}
          valor={String(player.assists)}
        />
        <NumeroDaTemporada
          rotulo={t("ficha.temporada.cartoes")}
          valor={`${player.yellow} / ${player.red}`}
          cor={player.red > 0 ? "var(--danger)" : player.yellow > 0 ? "var(--warning)" : undefined}
        />
      </div>
      </>
      )}

      <div className="ficha-fatos" style={comFigurinha ? undefined : { marginBottom: 12 }}>
        <Fact label={t("ficha.fatos.valor")} value={dinheiro(player.value)} />
        <Fact label={t("ficha.fatos.salario")} value={dinheiro(player.salary)} />
        <Fact
          label={t("ficha.fatos.contratoAte")}
          value={t("ficha.contratoValor", { temporada: player.contractEndsSeason })}
        />
        <Fact
          label={t("ficha.fatos.naCarreira")}
          value={t("ficha.jogosEGols", { jogos: player.careerApps, gols: player.careerGoals })}
        />
      </div>

      {!comFigurinha && (
      <div className="row" style={{ gap: 18, marginBottom: 16, flexWrap: "wrap" }}>
        <span className="row" style={{ gap: 8 }}>
          <span className="label-muted">{t("ficha.moral")}</span>
          <MoraleFace value={player.morale} />
          <span className="num">{player.morale}</span>
        </span>
        <span className="row" style={{ gap: 8 }}>
          <span className="label-muted">{t("ficha.fisico")}</span>
          <Bar value={player.condition} label={t("comum.condicao")} />
        </span>
        {player.rating > 0 && (
          <span className="row" style={{ gap: 8 }}>
            <span className="label-muted">{t("ficha.notaMedia")}</span>
            <span className="num" style={{ fontWeight: 700 }}>
              {player.rating.toFixed(1)}
            </span>
          </span>
        )}
      </div>
      )}

      {player.onLoan && (
        <div
          style={{
            margin: "0 0 16px",
            padding: "8px 12px",
            borderRadius: 8,
            background: "var(--surface-2)",
            borderLeft: "2px solid var(--accent)",
            fontSize: 12,
          }}
        >
          {t("ficha.emprestimo", {
            dono: player.onLoan.ownerName || t("comum.clubeDesconhecido"),
            tomador: player.onLoan.borrowerName || t("comum.clubeDesconhecido"),
            parte: Math.round(player.onLoan.salaryShare * 100),
          })}
        </div>
      )}

      {player.isMine ? (
        <>
      {/*
        As catorze características em duas abas, e não as catorze empilhadas.

        Empilhadas eram quatorze barras mais dois títulos, uns trezentos e
        cinquenta pixels que empurravam os botões de ação para fora da coluna e
        obrigavam a rolar para elogiar alguém. Em aba, a ficha cabe de uma vez.

        Abre nas que pesam, que são as quatro que decidem se ele joga bem na
        posição dele. As outras dez continuam a um toque, para quem está
        pensando em mudar alguém de posição.
      */}
      <div
        className="row ficha-abas"
        style={{ gap: 8, margin: comFigurinha ? "12px 0 8px" : "18px 0 10px" }}
      >
        {(["pesam", "demais"] as const).map((qual) => (
          <button
            key={qual}
            className="ghost"
            aria-pressed={abaDeAtributos === qual}
            onClick={() => definirAbaDeAtributos(qual)}
            style={{
              minHeight: 30,
              padding: "4px 10px",
              fontSize: 12,
              ...(abaDeAtributos === qual
                ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                : undefined),
            }}
          >
            {qual === "pesam"
              ? t("ficha.abaQuePesam", { total: weighted.length })
              : t("ficha.abaDemais", { total: others.length })}
          </button>
        ))}
      </div>

      {abaDeAtributos === "pesam" ? (
        <AttrGrid attrs={weighted} />
      ) : (
        <AttrGrid attrs={others} dim />
      )}

        <div
          className={comFigurinha ? "ficha-acoes" : "row"}
          style={comFigurinha ? undefined : { gap: 8, marginTop: 20, flexWrap: "wrap" }}
        >
          <button onClick={() => onAction("elogiar")} disabled={busy}>
            {t("ficha.elogiar")}
          </button>
          <button onClick={() => onAction("criticar")} disabled={busy}>
            {t("ficha.darBronca")}
          </button>
          <button onClick={() => onAction("renovar")} disabled={busy}>
            {t("ficha.renovarContrato")}
          </button>
          <button
            onClick={() => onAction(player.transferListed ? "tirar_lista" : "listar")}
            disabled={busy}
          >
            {player.transferListed ? t("ficha.tirarDaLista") : t("ficha.listarParaVenda")}
          </button>
        </div>
        </>
      ) : (
        /*
         * Proposta, empréstimo, características e olheiro em abas.
         *
         * Empilhados, passavam da altura da coluna e a ficha rolava. Em abas, a
         * aberta fica com o espaço que sobra embaixo dos números do jogador.
         * Começa na proposta, que é o que se veio fazer no mercado, e a escolha
         * vale para o próximo jogador que se abrir.
         */
        <div className="ficha-abas-de-fora">
          <div className="ficha-abas-barra" role="tablist">
            {abasDeFora.map(({ chave, rotulo }) => (
              <button
                key={chave}
                type="button"
                role="tab"
                aria-selected={abaDeFora === chave}
                onClick={() => definirSecao(chave)}
              >
                {rotulo}
              </button>
            ))}
          </div>
          <div className="ficha-aba-corpo" role="tabpanel">
            {abaDeFora === "olheiro" && (
              <ScoutBox player={player} busy={busy} onScout={onScout} embutido />
            )}
            {abaDeFora === "caracteristicas" && (
              <>
      {/*
        As catorze características em duas abas, e não as catorze empilhadas.

        Empilhadas eram quatorze barras mais dois títulos, uns trezentos e
        cinquenta pixels que empurravam os botões de ação para fora da coluna e
        obrigavam a rolar para elogiar alguém. Em aba, a ficha cabe de uma vez.

        Abre nas que pesam, que são as quatro que decidem se ele joga bem na
        posição dele. As outras dez continuam a um toque, para quem está
        pensando em mudar alguém de posição.
      */}
      <div
        className="row ficha-abas"
        style={{ gap: 8, margin: comFigurinha ? "12px 0 8px" : "18px 0 10px" }}
      >
        {(["pesam", "demais"] as const).map((qual) => (
          <button
            key={qual}
            className="ghost"
            aria-pressed={abaDeAtributos === qual}
            onClick={() => definirAbaDeAtributos(qual)}
            style={{
              minHeight: 30,
              padding: "4px 10px",
              fontSize: 12,
              ...(abaDeAtributos === qual
                ? { borderColor: "var(--primary)", color: "var(--primary)", fontWeight: 700 }
                : undefined),
            }}
          >
            {qual === "pesam"
              ? t("ficha.abaQuePesam", { total: weighted.length })
              : t("ficha.abaDemais", { total: others.length })}
          </button>
        ))}
      </div>

      {abaDeAtributos === "pesam" ? (
        <AttrGrid attrs={weighted} />
      ) : (
        <AttrGrid attrs={others} dim />
      )}

              </>
            )}
            {abaDeFora === "emprestimo" && (
              <>
                  {player.loanBlocked ? (
                    <p className="muted" style={{ margin: 0, fontSize: 12 }}>
                      {motivoDoMotor(player.loanBlocked.key, player.loanBlocked.params)}
                    </p>
                  ) : (
                    <>
                      <p className="muted" style={{ margin: "0 0 10px", fontSize: 12 }}>
                        {t("ficha.explicacaoDoEmprestimo", {
                          parte: Math.round(player.loanShare * 100),
                          valor: dinheiro(Math.round(player.salary * player.loanShare)),
                        })}
                      </p>
                      <button
                        disabled={busy || !marketOpen}
                        onClick={() => onLoan(player.loanShare)}
                      >
                        {marketOpen ? t("ficha.pedirEmprestado") : t("ficha.janelaFechada")}
                      </button>
                    </>
                  )}
              </>
            )}
            {abaDeFora === "proposta" && (
              <>
          {/*
            Leilão em andamento. O motor ouve as propostas da maior para a
            menor na virada da rodada, então saber que há disputa e por quanto
            é o que transforma a proposta numa decisão em vez de um chute.
          */}
          {player.auction && (
            <div
              style={{
                margin: "0 0 12px",
                padding: "8px 12px",
                borderRadius: 8,
                background: "var(--surface-2)",
                borderLeft: `2px solid ${player.auction.mineIsTop ? "var(--success)" : "var(--accent)"}`,
                fontSize: 12,
              }}
            >
              {t("ficha.leilao", {
                clubes: String(player.auction.bidders),
                maiorLance: dinheiro(player.auction.topBid),
              })}{" "}
              {player.auction.mineIsTop
                ? t("ficha.leilaoSeuLanceLidera")
                : t("ficha.leilaoPrecisaCobrir")}
            </div>
          )}

          {player.askingSalary === null ? (
            <p className="muted" style={{ margin: 0 }}>
              {t("ficha.naoDesceDePatamar", { nome: player.name })}
            </p>
          ) : (
            <>
              {/*
                Na coluna ao lado da lista a explicação sai: preço e salário
                pedido estão na linha da tabela, e o caixa no cabeçalho.
              */}
              {!naColuna && (
              <p className="muted" style={{ margin: "0 0 12px", fontSize: 12 }}>
                {t("ficha.explicacaoDaProposta", {
                  minimo: dinheiro(player.askingPrice),
                  salario: dinheiro(player.askingSalary),
                  caixa: dinheiro(cash),
                })}
              </p>
              )}
              {/*
                Comprar e propor convivem: o botão fecha na hora pelo preço que
                o clube aceita sem pensar; a proposta embaixo é para quem quer
                pagar menos e arriscar a recusa, a contraproposta ou o leilão.
              */}
              {onBuy && player.buyPrice !== null && (
                <button
                  className="primary"
                  style={{ width: "100%", marginBottom: 14 }}
                  disabled={busy || !marketOpen || player.buyPrice > cash}
                  onClick={() => onBuy(years)}
                >
                  {!marketOpen
                    ? t("ficha.janelaFechada")
                    : player.buyPrice === 0
                      ? t("ficha.contratarAgora")
                      : t("ficha.comprarPor", { valor: dinheiro(player.buyPrice) })}
                </button>
              )}
              <div className="row ficha-proposta-campos" style={{ gap: 10, flexWrap: "wrap" }}>
                <Field
                  label={t("ficha.valorDaCompra")}
                  value={amount}
                  step={100_000}
                  onChange={setAmount}
                />
                <Field
                  label={t("ficha.salarioPorRodada")}
                  value={salary}
                  step={5_000}
                  onChange={setSalary}
                />
                <label className="row" style={{ gap: 8 }}>
                  <span className="label-muted">{t("ficha.anos")}</span>
                  <select value={years} onChange={(e) => setYears(Number(e.target.value))}>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="row" style={{ gap: 8 }}>
                  <span className="label-muted">{t("ficha.parcelas")}</span>
                  <select
                    value={installments}
                    onChange={(e) => setInstallments(Number(e.target.value))}
                  >
                    <option value={1}>{t("ficha.aVista")}</option>
                    <option value={2}>{t("ficha.duasVezes")}</option>
                    <option value={3}>{t("ficha.tresVezes")}</option>
                  </select>
                </label>
              </div>

              {installments > 1 && (
                <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--accent)" }}>
                  {t("ficha.explicacaoDasParcelas", {
                    entrada: dinheiro(entrada),
                    restantes: installments - 1,
                  })}
                </p>
              )}

              <button
                className={onBuy && player.buyPrice !== null ? "ghost" : "primary"}
                style={{ marginTop: 14 }}
                disabled={busy || !marketOpen}
                onClick={() => onOffer(amount, salary, years, installments)}
              >
                {marketOpen ? t("ficha.enviarProposta") : t("ficha.janelaFechada")}
              </button>

            </>
          )}
              </>
            )}
          </div>
        </div>
      )}

      {/*
        Fechar só existe quando a ficha cobre a tela.

        Na coluna do elenco não há nada para fechar: a ficha some quando outra
        entra no lugar, e um botão que esvaziasse a coluna deixaria um vão de
        360 pixels sem motivo.
      */}
      {naColuna || (
        <button className="ghost" style={{ width: "100%", marginTop: 18 }} onClick={onClose}>
          {t("comum.fechar")}
        </button>
      )}
    </div>
  );
}

/**
 * Quanta fé se pode ter na faixa, pela sigla que o motor manda.
 *
 * A sigla é dado do laudo, como `alta` ou `baixa`, e chegava escrita na tela
 * exatamente assim, em português, para quem jogava em inglês.
 */
const CONFIANCAS: Record<string, Chave> = {
  alta: "ficha.confiancaAlta",
  "média": "ficha.confiancaMedia",
  baixa: "ficha.confiancaBaixa",
};

/**
 * Laudo de olheiro.
 *
 * O potencial continua invisível: o que aparece é a faixa em que ele cai, e a
 * largura dessa faixa é o que o nível da base compra. Sem laudo, a tela oferece
 * o custo de mandar observar.
 */
function ScoutBox({
  player,
  busy,
  onScout,
  embutido = false,
}: {
  player: PlayerDetail;
  busy: boolean;
  onScout: () => void;
  /** Dentro de uma seção da ficha, que já tem título e moldura. */
  embutido?: boolean;
}) {
  const t = useT();
  const scout = player.scout;

  return (
    <div
      style={
        embutido
          ? undefined
          : {
              margin: "0 0 16px",
              padding: "10px 12px",
              borderRadius: 8,
              background: "var(--surface-2)",
              border: "1px solid var(--border)",
            }
      }
    >
      <div className="row" style={{ justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        {!embutido && <span className="label">{t("ficha.olheiro")}</span>}
        {scout && (
          <span className="label-muted">
            {t(CONFIANCAS[scout.confidence] ?? "ficha.confiancaBaixa")}
            {scout.stale ? ` · ${t("ficha.laudoAntigo")}` : ""}
          </span>
        )}
      </div>

      {scout ? (
        <>
          <div className="row" style={{ gap: 10, alignItems: "baseline", marginTop: 6 }}>
            <span className="num" style={{ fontSize: 22, fontWeight: 800, color: "var(--primary)" }}>
              {t("ficha.faixa", { baixo: scout.low, alto: scout.high })}
            </span>
            <span className="muted" style={{ fontSize: 11.5 }}>
              {t("ficha.potencialEstimado")}
            </span>
          </div>
          <p className="muted" style={{ margin: "6px 0 0", fontSize: 12 }}>
            {t("ficha.laudoResumo", {
              minimo: scout.low,
              maximo: scout.high,
              veredito: rotuloDoMotor(scout.verdictKey),
            })}
          </p>
          <ul className="muted" style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 11.5 }}>
            {scout.notes.map((note, indice) => (
              <li key={note.key ?? `antiga-${indice}`}>{notaDeLaudo(note)}</li>
            ))}
          </ul>
          {scout.stale && (
            <button
              className="ghost"
              style={{ marginTop: 10 }}
              disabled={busy}
              onClick={onScout}
            >
              {t("ficha.observarDeNovo", { valor: dinheiro(player.scoutCost) })}
            </button>
          )}
        </>
      ) : (
        <>
          <p className="muted" style={{ margin: "6px 0 10px", fontSize: 12 }}>
            {t("ficha.semLaudo")}
          </p>
          <button disabled={busy} onClick={onScout}>
            {t("ficha.mandarObservar", { valor: dinheiro(player.scoutCost) })}
          </button>
        </>
      )}
    </div>
  );
}

function AttrGrid({ attrs, dim = false }: { attrs: PlayerDetail["attrs"]; dim?: boolean }) {
  return (
    <div
      className="grid"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 6 }}
    >
      {attrs.map((attr) => (
        <div key={attr.key} className="row" style={{ gap: 8, opacity: dim ? 0.6 : 1 }}>
          <span className="muted" style={{ flex: 1, fontSize: 11.5 }}>
            {rotuloDeCaracteristica(attr.key)}
          </span>
          <span
            style={{
              width: 52,
              height: 5,
              borderRadius: 999,
              background: "var(--surface-3)",
              overflow: "hidden",
            }}
          >
            <span
              style={{
                display: "block",
                width: `${attr.value}%`,
                height: "100%",
                background: strengthColor(attr.value),
              }}
            />
          </span>
          <span className="num" style={{ width: 24, fontWeight: 600 }}>
            {attr.value}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Um dado sobre a foto: rótulo pequeno em cima, valor embaixo, em vidro. */
function SeloNaFoto({
  rotulo,
  cor,
  children,
}: {
  rotulo: string;
  cor?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="ficha-selo">
      <small>{rotulo}</small>
      <b className="num" style={cor ? { color: cor } : undefined}>
        {children}
      </b>
    </div>
  );
}

function NumeroDaTemporada({
  rotulo,
  valor,
  cor,
}: {
  rotulo: string;
  valor: string;
  cor?: string | undefined;
}): ReactNode {
  return (
    <div>
      <div className="num" style={{ fontSize: 22, fontWeight: 800, color: cor }}>
        {valor}
      </div>
      <div className="label-muted">{rotulo}</div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="label-muted">{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Field({
  label,
  value,
  step,
  onChange,
}: {
  label: string;
  value: number;
  step: number;
  onChange: (value: number) => void;
}) {
  return (
    <label style={{ minWidth: 150 }}>
      <div className="label-muted" style={{ marginBottom: 4 }}>
        {label}
      </div>
      {/* O valor por extenso fica dentro do campo, à direita, e não numa linha a mais. */}
      <span className="campo-com-valor">
        <input
          type="number"
          min={0}
          step={step}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value)))}
          style={{ width: "100%" }}
        />
        <span className="muted num">{dinheiro(value)}</span>
      </span>
    </label>
  );
}
